"use client"

import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2, Save, CheckCircle, AlertCircle, Eye, EyeOff, Trash2, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react"
import { useTwilioIntegration } from "@/hooks/integrations/use-twilio-integration"
import { useTranslations } from "next-intl"



interface TwilioCredentialsFormProps {
  onSave: () => void
  onCancel?: () => void
  experience?: "settings" | "onboarding"
}

export function TwilioCredentialsForm({
  onSave,
  onCancel,
  experience = "settings",
}: TwilioCredentialsFormProps) {

  const tUi = useTranslations("Integrations.IntegrationsTwilioTwilioCredentialsForm")
  const tCommonUi = useTranslations("Common")

  const { integration, isLoading, isConfigured, saveCredentials, deleteIntegration } = useTwilioIntegration()

  const [accountSid, setAccountSid] = useState("")
  const [authToken, setAuthToken] = useState("")
  const [showAuthToken, setShowAuthToken] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  // When the integration is already connected, the form below is for credential
  // rotation only. Default to collapsed so the dialog surfaces status + remove
  // controls, not the input fields — those were the source of the "am I about
  // to create a second account?" confusion in user testing. First-time setup
  // (`!isConfigured`) keeps the form expanded by default.
  //
  // `useTwilioIntegration()` resolves asynchronously, so on the very first
  // render `isConfigured=false` (hook still loading). Naively deriving from
  // `isConfigured` would briefly flash the credential fields before the hook
  // resolves — a visible regression of the "hide fields when connected" UX.
  //
  // Three-way state, evaluated in order of precedence:
  //   - `userOverride !== null` → user explicitly clicked the toggle, respect
  //     their choice across subsequent re-renders.
  //   - `isLoading` → hide fields until the dialog's integration check
  //     resolves. This avoids briefly showing first-time credential inputs
  //     when the tenant is already connected.
  //   - Otherwise → derive from `isConfigured`.
  const [userOverride, setUserOverride] = useState<boolean | null>(null)
  const showCredentialFields =
    userOverride ?? (isLoading ? false : !isConfigured)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    if (!accountSid.trim()) {
      setError(tUi("accountSIDIsRequired"))
      return
    }

    if (!authToken.trim()) {
      setError(tUi("authTokenIsRequired"))
      return
    }

    // Validate Account SID format (starts with AC)
    if (!accountSid.startsWith("AC")) {
      setError(tUi("accountSIDShouldStartWithAC"))
      return
    }

    setIsSaving(true)

    try {
      await saveCredentials(accountSid.trim(), authToken.trim())
      setAccountSid("")
      setAuthToken("")
      onSave()
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToSaveCredentials"))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false)
    setIsDeleting(true)
    setError(null)

    try {
      await deleteIntegration()
      setSuccess("Twilio integration removed successfully")
      // Close form and refresh parent after short delay
      setTimeout(() => {
        onSave()
      }, 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToRemoveIntegration"))
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Current Status */}
      {isConfigured && integration && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg overflow-hidden">
          <div className="flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="font-medium"><span key={tUi("twilioConnected")}>{tUi("twilioConnected")}</span></span>
          </div>
          {integration.account_name && (
            <p className="text-sm text-green-700 mt-1 truncate">
              <span><span key={tUi("account")}>{tUi("account")}</span></span> {integration.account_name}
            </p>
          )}
        </div>
      )}

      {integration?.status === 'error' && integration.validation_error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">
            {integration.validation_error}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert className="border-red-200 bg-red-50">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      {/* When already connected: toggle to surface the rotate-credentials form.
          Hides the input fields by default so the dialog reads as «you're
          connected; here's what to do next», not «enter credentials again». */}
      {isConfigured && (
        <button
          type="button"
          onClick={() => setUserOverride(!showCredentialFields)}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {showCredentialFields ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
          <span key={showCredentialFields ? tUi("hideCredentialFields") : tUi("rotateOrReplaceCredentials")}>{showCredentialFields ? tUi("hideCredentialFields") : tUi("rotateOrReplaceCredentials")}</span>
        </button>
      )}

      <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
        {/* Hidden by default for connected tenants; revealed via the toggle
            above. First-time setup keeps everything inline. The Remove
            Integration button lives lower down OUTSIDE this collapsible
            block so it stays reachable when fields are hidden. */}
        <div className={!showCredentialFields ? 'hidden' : 'space-y-4'}>
        {isConfigured && (
          <p className="text-xs text-muted-foreground">
            <span key={tUi("enterNewValuesBelowToReplaceTheStored")}>{tUi("enterNewValuesBelowToReplaceTheStored")}</span></p>
        )}
        <div className="space-y-2">
          <Label htmlFor="accountSid"><span key={"Account SID *"}>{"Account SID *"}</span></Label>
          <Input
            id="accountSid"
            name="twilio_account_sid_field"
            placeholder={"ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"}
            value={accountSid}
            onChange={(e) => setAccountSid(e.target.value)}
            autoComplete="off"
            data-lpignore="true"
            data-1p-ignore
          />
          <p className="text-xs text-gray-500">
            <span key={tUi("foundInYourTwilioConsoleDashboard")}>{tUi("foundInYourTwilioConsoleDashboard")}</span></p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="authToken"><span key={"Auth Token *"}>{"Auth Token *"}</span></Label>
          <div className="relative">
            <Input
              id="authToken"
              name="twilio_auth_token_field"
              type={showAuthToken ? "text" : "password"}
              placeholder={tUi("yourTwilioAuthToken")}
              value={authToken}
              onChange={(e) => setAuthToken(e.target.value)}
              className="pr-10"
              autoComplete="new-password"
              data-lpignore="true"
              data-1p-ignore
            />
            <button
              type="button"
              onClick={() => setShowAuthToken(!showAuthToken)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showAuthToken ? (
                <EyeOff className="w-4 h-4" />
              ) : (
                <Eye className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500">
            <span key={tUi("foundInYourTwilioConsoleDashboard")}>{tUi("foundInYourTwilioConsoleDashboard")}</span></p>
        </div>
        </div>{/* /credential-fields-collapsible */}

        <div className="pt-4 border-t space-y-3">
          <div className="flex justify-end gap-3">
            {onCancel ? (
              <Button type="button" variant="outline" onClick={onCancel}>
                <span key={!showCredentialFields ? tUi("close") : tCommonUi("cancel")}>{!showCredentialFields ? tUi("close") : tCommonUi("cancel")}</span>
              </Button>
            ) : null}
            {/* Save submits the form — only meaningful when fields are visible.
                When collapsed (already-configured tenant), the user isn't
                providing new credentials so we hide the submit entirely. */}
            {showCredentialFields && (
              <Button type="submit" disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    <span key={tCommonUi("saving")}>{tCommonUi("saving")}</span></>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    <span key={isConfigured ? tUi("update") : tUi("save")}>{isConfigured ? tUi("update") : tUi("save")}</span>
                  </>
                )}
              </Button>
            )}
          </div>

          {isConfigured && experience === "settings" ? (
            <div className="flex justify-start">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={handleDeleteClick}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                <span><span key={tUi("removeIntegration")}>{tUi("removeIntegration")}</span></span>
              </Button>
            </div>
          ) : null}
        </div>
      </form>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <AlertDialogTitle><span key={tUi("removeTwilioIntegration")}>{tUi("removeTwilioIntegration")}</span></AlertDialogTitle>
            </div>
            <AlertDialogDescription asChild>
              <div className="pt-2 space-y-2 text-sm text-muted-foreground">
                <p><span key={tUi("areYouSureYouWantToRemoveThis")}>{tUi("areYouSureYouWantToRemoveThis")}</span></p>
                <p className="font-medium text-foreground"><span key={tUi("thisWillAlsoDelete")}>{tUi("thisWillAlsoDelete")}</span></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><span key={tUi("allPhoneNumberConfigurations")}>{tUi("allPhoneNumberConfigurations")}</span></li>
                  <li><span key={tUi("allCallFlows")}>{tUi("allCallFlows")}</span></li>
                  <li><span key={tUi("allOperatorGroups")}>{tUi("allOperatorGroups")}</span></li>
                </ul>
                <p className="pt-2"><span key={tUi("voiceAgentsWillNoLongerBeAbleTo")}>{tUi("voiceAgentsWillNoLongerBeAbleTo")}</span></p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              <span key={tUi("removeIntegration")}>{tUi("removeIntegration")}</span></AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
